import type { AdSetSelectionEntry } from 'hooks/useLocationParams';
import { normalizeFlowsString } from 'utils';
import { orderByIds } from './orderByIds';
import { isAdSetFlowEmpty } from './adSetFlowCache';
import type { AdSetEntry, RowStatus } from '../api';

// `AdSetEntry.status` is a loose `string` (server-typed), unlike
// `CampaignEntry.status`, which is already narrowed to `RowStatus` — so,
// unlike `toCampaignSelectionEntries`, this needs a runtime check before
// it can be used as a `RowStatus`.
const isRowStatus = (value: string | undefined): value is RowStatus =>
  value === 'ACTIVE' || value === 'PAUSED';

export interface RestoredAdSetSelection {
  selectedAdSets: AdSetEntry[];
  selectedAdSetsBudgets: Record<string, string>;
  selectedAdSetsStartTimes: Record<string, string>;
  selectedAdSetsDuplicatesCount: Record<string, string>;
  selectedAdSetsFlows: Record<string, string>;
}

const applyUrlFlowToAdSet = (
  row: AdSetEntry,
  entry: AdSetSelectionEntry,
): AdSetEntry => {
  const next: AdSetEntry = entry.status ? { ...row, status: entry.status } : { ...row };
  const urlFlow = normalizeFlowsString(entry.flow);
  if (isAdSetFlowEmpty(urlFlow) || !urlFlow) {
    return next;
  }

  const isUserAssigned =
    isAdSetFlowEmpty(row.flow) || normalizeFlowsString(row.flow) !== urlFlow;

  return {
    ...next,
    flow: urlFlow,
    ...(isUserAssigned ? { flowAssignedByUser: true } : {}),
  };
};

/**
 * Restores a deep-linked/shared ad set selection once real ad set rows are
 * available (they're fetched asynchronously — and depend on the selected
 * campaigns — so this can't happen at form-default time). Ids that no
 * longer exist among the fetched rows are dropped; budget is only restored
 * for ABO ad sets, matching what the budget column actually persists.
 * User-assigned flows (including multi-flow `"flowA,flowB"` strings) are
 * applied onto template rows and recorded in `selectedAdSetsFlows`.
 */
export const restoreSelectedAdSetsFromUrl = (
  entries: readonly AdSetSelectionEntry[],
  rows: readonly AdSetEntry[],
): RestoredAdSetSelection => {
  const entryById = new Map(entries.map((entry) => [entry.id, entry]));
  const selectedAdSets: AdSetEntry[] = [];
  const selectedAdSetsBudgets: Record<string, string> = {};
  const selectedAdSetsStartTimes: Record<string, string> = {};
  const selectedAdSetsDuplicatesCount: Record<string, string> = {};
  const selectedAdSetsFlows: Record<string, string> = {};

  const orderedRows = orderByIds(
    rows.filter((row) => entryById.has(String(row.id))),
    entries.map(({ id }) => id),
  );

  orderedRows.forEach((row) => {
    const entry = entryById.get(String(row.id));
    if (!entry) return;

    const restored = applyUrlFlowToAdSet(row, entry);
    selectedAdSets.push(restored);

    if (restored.flowAssignedByUser && restored.flow) {
      selectedAdSetsFlows[row.id] = restored.flow;
    }

    if (!row.is_cbo && entry.budget) {
      selectedAdSetsBudgets[row.id] = entry.budget;
    }
    if (entry.startTime) {
      selectedAdSetsStartTimes[row.id] = entry.startTime;
    }
    if (entry.duplicatesCount) {
      selectedAdSetsDuplicatesCount[row.id] = entry.duplicatesCount;
    }
  });

  return {
    selectedAdSets,
    selectedAdSetsBudgets,
    selectedAdSetsStartTimes,
    selectedAdSetsDuplicatesCount,
    selectedAdSetsFlows,
  };
};

/** Mirrors the current selection (+ ABO budget/start time/duplicate count/user flow) back into the URL's shape. */
export const toAdSetSelectionEntries = (
  rows: readonly AdSetEntry[],
  budgets: Record<string, string> | undefined,
  startTimes: Record<string, string> | undefined,
  duplicatesCount: Record<string, string> | undefined,
): AdSetSelectionEntry[] =>
  rows.map((row) => ({
    id: String(row.id),
    budget: budgets?.[row.id],
    status: isRowStatus(row.status) ? row.status : undefined,
    startTime: startTimes?.[row.id],
    duplicatesCount: duplicatesCount?.[row.id],
    flow:
      row.flowAssignedByUser && !isAdSetFlowEmpty(row.flow)
        ? (normalizeFlowsString(row.flow) ?? undefined)
        : undefined,
  }));
